import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CalendarWithMarks from '../components/DailyRecord/CalendarWithMarks';
import DailyRecordForm from '../components/DailyRecord/DailyRecordForm';
import EvaluationForm from '../components/DailyRecord/EvaluationForm';
import MonthlyEvaluationForm from '../components/DailyRecord/MonthlyEvaluationForm';
import SupportPlanModal from '../components/DailyRecord/SupportPlanModal';
import { getUserRole } from '../components/Auth';
import '../styles/DailyRecord.css';

const DailyRecord = () => {
  const { loginCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('daily');
  const [marks, setMarks] = useState({});
  const [userName, setUserName] = useState('');
  const [lastEvalDate, setLastEvalDate] = useState(null);
  const [monthlyMarks, setMonthlyMarks] = useState({});
  const [lastMonthlyEval, setLastMonthlyEval] = useState(null);

  const [showSupportPlan, setShowSupportPlan] = useState(false);
  const [supportPlan, setSupportPlan] = useState({
    longTermGoal: '',
    shortTermGoal: '',
    needs: '',
    supportContent: '',
    goalDate: '',
  });
  const [isWarning, setIsWarning] = useState(false);


  // 認可・ユーザー名初期化
  useEffect(() => {
    const role = getUserRole();
    if (role < 5) {
      alert('このページは管理者以上のみ閲覧可能です');
      navigate('/');
      return;
    }
    if (location.state?.userName) {
      setUserName(location.state.userName);
    }
  }, [navigate, location.state]);

  const fetchSupportPlan = useCallback(async () => {
    try {
      const res = await fetch('https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get-support-plan',
          login_code: loginCode,
        }),
      });

      const data = await res.json();
      const supportData = data.body || data || {};

      setSupportPlan({
        longTermGoal: supportData.long_term_goal || '',
        shortTermGoal: supportData.short_term_goal || '',
        needs: supportData.needs || '',
        supportContent: supportData.support_content || '',
        goalDate: supportData.goal_date ? supportData.goal_date.split('T')[0] : '',
      });

    } catch (err) {
      console.error('📋 個別支援計画の取得失敗:', err);
    }
  }, [loginCode]);

  useEffect(() => {
    if (supportPlan.goalDate) {
      const today = new Date();
      const goal = new Date(`${supportPlan.goalDate}T00:00:00`);
      const diff = Math.floor((goal - today) / (1000 * 60 * 60 * 24));
      setIsWarning(diff <= 30 && diff >= 0);
    }
  }, [supportPlan.goalDate]);

  // 🔹 マーク（viewModeごとに切り替え）
  const fetchMarkedDates = useCallback(async () => {
    try {
      const action = viewMode === 'daily' ? 'get-record-marks' : 'get-weekly-marks';

      const res = await fetch('https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, login_code: loginCode }),
      });

      const data = await res.json();
      const marksData = data.body || data || {};
      setMarks(marksData);

    } catch (err) {
      console.error('📋 マーク取得失敗:', err);
      alert('記録マークの取得に失敗しました');
    }
  }, [loginCode, viewMode]);

  // 🔹 常時：週報の最終評価日を取得（別APIコール）
  const fetchLatestEvaluationDate = useCallback(async () => {
    try {
      const res = await fetch('https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-weekly-marks', login_code: loginCode }),
      });

      const data = await res.json();
      const weeklyMarks = data.body || data || {};
      const latestDate = Object.keys(weeklyMarks).sort().reverse()[0] || null;
      setLastEvalDate(latestDate);
    } catch (err) {
      console.error('📋 週報の最終評価日取得失敗:', err);
    }
  }, [loginCode]);

  const fetchMonthlyMarks = useCallback(async () => {
    try {
      const res = await fetch('https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-monthly-marks', login_code: loginCode })
      });

      const data = await res.json();
      const monthlyData = data.body || data || {};
      
      setMonthlyMarks(monthlyData);

      // 最終月次評価日の抽出（キーが "YYYY-MM-DD" 形式である前提）
      const latestDate = Object.keys(monthlyData).sort().reverse()[0] || null;
      setLastMonthlyEval(latestDate);

    } catch (err) {
      console.error('📋 月報マーク取得失敗:', err);
    }
  }, [loginCode]);

  useEffect(() => {
    if (loginCode && viewMode) {
      fetchMarkedDates();
    }
  }, [loginCode, viewMode, fetchMarkedDates]);

  useEffect(() => {
    if (loginCode) {
      fetchLatestEvaluationDate();
    }
  }, [loginCode, fetchLatestEvaluationDate]);

  useEffect(() => {
    if (loginCode) {
      fetchMonthlyMarks();
    }
  }, [loginCode, fetchMonthlyMarks]);

  useEffect(() => {
    if (loginCode) {
      fetchSupportPlan();
    }
  }, [loginCode, fetchSupportPlan]);

  const formatJPDate = (input) =>
    new Date(input).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
    });  

  return (
    <div className="daily-record-page">
      <h1 className="page-title">在宅記録ページ</h1>

      <div className="login-code-display">
        対象者：{userName || '---'}（{loginCode}）
        <button
          className={`support-plan-button ${
            isWarning ? 'warning' : ''
          }`}
          onClick={() => setShowSupportPlan(true)}
        >
          個別支援計画の確認・編集
        </button>
      </div>

      <div className="selected-date-info">
        <div className="selected-date-box">
          選択中の日付：{formatJPDate(selectedDate)}
        </div>
        <div className="last-eval-box">
          最終週次評価日：{lastEvalDate ? formatJPDate(lastEvalDate) : '（記録なし）'}
        </div>
        <div className="last-monthly-eval-box">
          最終就労達成度評価月：
          {lastMonthlyEval
            ? new Date(lastMonthlyEval).toLocaleDateString('ja-JP', {
                year: 'numeric',
                month: 'long',
              })
            : '（記録なし）'}
        </div>
      </div>

      <div className="mode-toggle">
        <button
          className={viewMode === 'daily' ? 'active' : ''}
          onClick={() => setViewMode('daily')}
        >
          就労支援記録の確認・編集
        </button>
        <button
          className={viewMode === 'evaluation' ? 'active' : ''}
          onClick={() => setViewMode('evaluation')}
        >
          週次評価の確認・編集
        </button>
        <button
          className={viewMode === 'monthly' ? 'active' : ''}
          onClick={() => setViewMode('monthly')}
        >
          在宅における就労達成度評価シートの確認・編集
        </button>
      </div>

      <div className="calendar-container">
        <CalendarWithMarks
          dailyMarks={viewMode === 'daily' ? marks : {}}
          weeklyMarks={viewMode === 'evaluation' ? marks : {}}
          onSelectDate={setSelectedDate}
        />
      </div>

      <div className="form-container">
        {viewMode === 'daily' && (
          <DailyRecordForm
            loginCode={loginCode}
            date={selectedDate}
            onSaved={fetchMarkedDates}
          />
        )}
        {viewMode === 'evaluation' && (
          <EvaluationForm
            loginCode={loginCode}
            date={selectedDate}
            onSaved={fetchMarkedDates}
          />
        )}
        {viewMode === 'monthly' && (
          <MonthlyEvaluationForm
            loginCode={loginCode}
            userName={userName}
            selectedDate={selectedDate} 
            onSaved={fetchMarkedDates}
            onDateChange={setSelectedDate}
          />
        )}
      </div>

      <div>
        <button className="custom-button" onClick={() => navigate('/admin')}>
          管理者ページに戻る
        </button>
      </div>

      <SupportPlanModal
        show={showSupportPlan}
        onClose={() => setShowSupportPlan(false)}
        loginCode={loginCode}
        onUpdated={fetchSupportPlan}
      />
    </div>
  );
};

export default DailyRecord;
